import { ColorEnum } from '@/constants/color'
import ListItemFactory from '@/features/notifications/components/NotificationCenter/parts/ListItem/Factory'
import { handlePressAction } from '@/features/notifications/components/NotificationCenter/parts/ListItem/thunks/handlePressAction'
import { OnPress } from '@/features/notifications/components/NotificationCenter/parts/ListItem/types'
import { ReadNotifications } from '@/features/notifications/components/NotificationCenter/types'
import { NotificationType } from '@/features/notifications/notification-service-extension-shared/constants/app'
import { Notification } from '@/features/notifications/notification-service-extension-shared/types/app'
import Typography from '@/features/ui/typography'
import { useAppDispatch } from '@/hooks/redux'
import { useScreenLayout } from '@/hooks/screen'
import { FlashList, ListRenderItem } from '@shopify/flash-list'
import { FC, RefObject, memo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { View, ViewProps } from 'react-native'
import { createStyleSheet, useStyles } from 'react-native-unistyles'

interface NotificationListProps extends ViewProps {
    notifications: Notification[]
    readNotifications: ReadNotifications
    close: () => void
    ref: RefObject<View | null>
}

const keyExtractor = (item: Notification): string => item.id

const getItemType = (item: Notification): NotificationType => item.type

const NotificationList: FC<NotificationListProps> = ({
    notifications,
    readNotifications,
    close,
    ref,
    ...props
}) => {
    const dispatch = useAppDispatch()

    const screenLayout = useScreenLayout()

    const { t } = useTranslation()

    const { styles } = useStyles(stylesheet, { layout: screenLayout })

    const handlePress = useCallback<OnPress>(
        (notification, read) => {
            dispatch(handlePressAction({ notification, read, close }))
        },
        [dispatch, close]
    )

    const renderItem: ListRenderItem<Notification> = useCallback(
        ({ item: notification }) => (
            <ListItemFactory
                notification={notification}
                read={!!readNotifications[notification.id]}
                onPress={handlePress}
            />
        ),
        [readNotifications, handlePress]
    )

    return (
        <View {...props} style={styles.outerContainer} ref={ref}>
            <View style={styles.innerContainer}>
                <FlashList
                    data={notifications}
                    renderItem={renderItem}
                    keyExtractor={keyExtractor}
                    estimatedItemSize={71}
                    showsVerticalScrollIndicator={false}
                    ListHeaderComponent={
                        <Typography.Heading variant="h3">
                            {t('notificationCenter.heading')}
                        </Typography.Heading>
                    }
                    ListHeaderComponentStyle={styles.typography}
                    getItemType={getItemType}
                    contentContainerStyle={styles.listContentContainer}
                />
            </View>
        </View>
    )
}

const stylesheet = createStyleSheet((_, { insets }) => ({
    outerContainer: {
        flex: 1,
        backgroundColor: ColorEnum.GRAY_100,
        variants: {
            layout: {
                extended: {
                    borderRadius: 16,
                    elevation: 8,
                    shadowColor: 'rgba(0, 0, 0, 0.25)',
                    shadowOffset: { width: 0, height: 0 },
                    shadowOpacity: 1,
                    shadowRadius: 8,
                },
                compact: {
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                },
            },
        },
    },
    innerContainer: {
        flex: 1,
        overflow: 'hidden',
        variants: {
            layout: {
                extended: {
                    borderRadius: 16,
                },
                compact: {
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                },
            },
        },
    },
    typography: {
        paddingTop: 24,
        paddingBottom: 16,
        paddingHorizontal: 16,
    },
    listContentContainer: {
        paddingBottom: insets.bottom,
    },
}))

export default memo(NotificationList)
